// Notes and report of a remote assistance (`Docs/Jamora Assist/PROPOSITION_NOTES_ASSISTANCE.md`): field list and the pure
// rules of the shared note. One note per assistance, edited by several technicians: each field is saved alone with its own
// version, a field being typed is never replaced under the technician's fingers, and a colleague's newer text on that same field
// becomes a conflict the technician settles (keep mine / take theirs) — never a silent overwrite either way.
import { hasPermission } from "./permissions";

export const NOTE_FIELDS = [
  { key: "problem", label: "Problème constaté", max: 2000, rows: 3,
    placeholder: "Ce que le pharmacien a signalé, ce que vous avez constaté…" },
  { key: "solution", label: "Solution apportée", max: 2000, rows: 3,
    placeholder: "Ce qui a été fait, comment le problème a été réglé…" },
  { key: "feedback", label: "Suggestions et remarques du pharmacien", max: 2000, rows: 3,
    placeholder: "Idées, demandes, façons de faire qu'il a partagées…" },
  { key: "notes", label: "Notes libres", max: 20000, rows: 6, placeholder: "Tout le reste." },
];

export const NOTE_SAVE_DELAY_MS = 1000;
export const NOTES_STORAGE_KEY = "jamora.assist.notesOpen";
export const NOTES_COLUMN_MIN_WIDTH = 1280;

const EMPTY_SERVER = { value: "", version: 0, updated_by: null, updated_at: null };

// Local state of one field: what is in the textarea (`value`), the server text it started from (`base`) and its `version`.
export function fieldFromServer(server = EMPTY_SERVER) {
  return { value: server.value || "", base: server.value || "", version: server.version || 0,
    updated_by: server.updated_by || null, updated_at: server.updated_at || null, conflict: null };
}

export const isDirty = (field) => field.value !== field.base;

// A server read (polling) arriving while the field may be in use. `busy` = a save of this field is in flight: its own answer will
// bring the new version, a read taken meanwhile could be older.
export function mergeField(local, server = EMPTY_SERVER, { busy = false } = {}) {
  if (busy || (server.version || 0) <= local.version) return local;
  if (!isDirty(local)) return fieldFromServer(server);
  if (server.value === local.value) return afterSave(local, server);
  return { ...local, conflict: { ...EMPTY_SERVER, ...server } };
}

// After a save (or a read holding exactly what is typed): the server now has `server`; whatever was typed meanwhile stays.
export function afterSave(local, server) {
  return { ...local, base: server.value || "", version: server.version || 0,
    updated_by: server.updated_by || null, updated_at: server.updated_at || null, conflict: null };
}

// Settling a conflict. Keep mine: write over the version just seen. Take theirs: the colleague's text becomes mine.
export const keepMine = (local) => ({ ...local, version: local.conflict.version, conflict: null });
export const takeTheirs = (local) => fieldFromServer(local.conflict);

export function hasUnsaved(fields) {
  return Object.values(fields || {}).some((field) => isDirty(field) || field.conflict);
}

export function noteIsEmpty(note) {
  return !note || NOTE_FIELDS.every(({ key }) => !(note.fields?.[key]?.value || "").trim());
}

function hourMinute(iso) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const sameDay = date.toDateString() === new Date().toDateString();
  return sameDay
    ? `à ${date.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`
    : `le ${date.toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}`;
}

export function updatedLabel(entry) {
  if (!entry?.updated_by || !entry?.updated_at) return "";
  return `Modifié par ${entry.updated_by.name || "un compte supprimé"} ${hourMinute(entry.updated_at)}`.trim();
}

export function conflictLabel(conflict) {
  const who = conflict?.updated_by?.name || "Un collègue";
  const when = conflict?.updated_at ? ` ${hourMinute(conflict.updated_at)}` : "";
  return `${who} a modifié ce champ${when}.`;
}

// What a list row shows of a report (`GET /assist/notes` gives each field as plain text): the solution first — what someone
// looking for a fix wants —, else the first field that holds text.
const EXCERPT_ORDER = ["solution", "problem", "feedback", "notes"];
export function reportExcerpt(item) {
  for (const key of EXCERPT_ORDER) {
    const text = (item?.fields?.[key] || "").trim();
    if (text) return { label: NOTE_FIELDS.find((f) => f.key === key).label, text };
  }
  return null;
}

// Who may write (the server decides again): the session's technicians — principal or added, present or gone — and
// `support:view_all` holders; a request nobody took has no report yet.
export function canWriteNotes(user, item) {
  if (!user || !item?.technician) return false;
  if (hasPermission(user, "support:view_all")) return true;
  return item.technician.id === user.id || (item.co_technicians || []).some((c) => c.id === user.id);
}

// The column next to the screen is open by default only where it leaves the picture enough room; the technician's own choice wins.
export function initialColumnOpen(storage, width) {
  try {
    const saved = storage?.getItem(NOTES_STORAGE_KEY);
    if (saved === "1" || saved === "0") return saved === "1";
  } catch {
    // storage blocked (private window): fall back to the width
  }
  return width >= NOTES_COLUMN_MIN_WIDTH;
}
